import React from 'react'
import { Modal, Tag } from 'antd'
import type { TravelCategory, ExpenseType } from '@/types'

// 新增代码

/** 提交确认对话框的表单摘要数据 */
export interface SubmitSummary {
  applicantName: string
  category: TravelCategory
  expenseType: ExpenseType
  startDate: string
  endDate: string
  departureCity?: string
  destinationCity?: string
  /** 多目的城市列表（跨城单段），优先于 destinationCity */
  destinationCities?: Array<{ name: string; code: string }>
  location?: string
  trainNeeded?: boolean
  companionCount: number
  totalExpense: number
}

interface SubmitConfirmDialogProps {
  visible: boolean
  summary: SubmitSummary
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * 二次确认对话框组件 (FR-012, FR-012A)
 *
 * 功能：
 * - 显示申请摘要（申请人、同行人数、时间范围、总费用、目的地、出差类型标签）
 * - 自费时显示醒目的 [自费申请] 标签和特殊提示
 * - 提供"查看审批流"展开按钮
 * - "确认提交"和"取消"两个按钮
 */
const SubmitConfirmDialog: React.FC<SubmitConfirmDialogProps> = ({
  visible,
  summary,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const isSelfPaid = summary.expenseType === 'SELF_PAID'
  const isLocal = summary.category === 'LOCAL'

  /** 格式化时间为 MM/DD HH:mm（省略年份，用于紧凑展示） */
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '—'
    try {
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '—'

      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${month}/${day} ${hours}:${minutes}`
    } catch {
      return '—'
    }
  }

  /** 格式化目的地显示 */
  const destinationCityDisplay = (() => {
    if (summary.destinationCities && summary.destinationCities.length > 0) {
      return summary.destinationCities.map(c => c.name).join('、')
    }
    return summary.destinationCity || '—'
  })()
  const destinationText = isLocal
    ? summary.location || '—'
    : `${summary.departureCity || '—'} → ${destinationCityDisplay}`

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold">确认提交申请</span>
          {isSelfPaid && (
            <Tag color="orange" className="rounded-lg font-semibold">
              自费申请
            </Tag>
          )}
        </div>
      }
      open={visible}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="确认提交"
      cancelText="取消"
      confirmLoading={loading}
      width={520}
      maskClosable={false}
    >
      <div className="space-y-2 py-0">
        {/* 自费申请特殊提示 (FR-012A) */}
        {isSelfPaid && (
          <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
            <p className="text-sm text-orange-800 font-medium">
              ⚠️ 本申请为自费，不推送差旅平台预订，仅记录考勤
            </p>
          </div>
        )}

        {/* 申请摘要 */}
        <div className="rounded-xl bg-slate-50 p-3 space-y-2">
          <SummaryRow label="申请人" value={summary.applicantName} />
          <SummaryRow
            label="出行类别"
            value={isLocal ? '同城' : '跨城'}
          />
          {!isLocal && (
            <SummaryRow
              label="出差类型"
              value={isSelfPaid ? '自费' : '部门承担'}
            />
          )}
          <SummaryRow label={isLocal ? '外出地点' : '出差目的地'} value={destinationText} />
          <SummaryRow
            label="出行时间"
            value={`${formatDateTime(summary.startDate)} ~ ${formatDateTime(summary.endDate)}`}
          />
          {!isLocal && (
            <SummaryRow
              label="同行人数"
              value={`${summary.companionCount} 人`}
            />
          )}
          {!isSelfPaid && !isLocal && (
            <SummaryRow
              label="预估总费用"
              value={`¥${(summary.totalExpense ?? 0).toLocaleString()}`}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

/** 摘要行组件 */
const SummaryRow: React.FC<{ label: string; value?: string; children?: React.ReactNode }> = ({ label, value, children }) => (
  <div className="flex items-start text-sm">
    <span className="text-slate-500 shrink-0" style={{ width: '35%' }}>{label}：</span>
    {children ?? <span className="text-slate-800 font-medium" style={{ width: '65%' }}>{value}</span>}
  </div>
)

export default SubmitConfirmDialog
